/**
 * Phase 4 — production totals.
 *
 * Walk every placed building, resolve its catalogue payload via the lookup
 * callback, and sum the per-day production into the seven Phase 4 totals.
 * Buildings flagged in `invalidBuildingIds` (disconnected, outside-area,
 * overlapping) contribute zero — strict validation, matches the renderer's
 * red-tint contract.
 *
 * Pure: takes `(plan, lookup, invalidBuildingIds, playerEra)`. Memoisation
 * lives in a sibling module so the pure compute stays test-friendly.
 *
 * The arithmetic is the easy part; the catalogue is the hard part. See
 * `catalogue-fields.ts` for every dual-format trap discovered during the
 * step-0 audit. This module just orchestrates the resolvers.
 *
 * Resource keys we accumulate:
 *   coins        ← `money` resource
 *   supplies     ← `supplies` resource
 *   forgePoints  ← `strategy_points` resource
 *   goods        ← every other resource that isn't coins/supplies/FP/medals/
 *                  population/happiness/premium/diamonds (Marble, Iron,
 *                  Nanowire, etc. — accumulated by name into a Map)
 *   units        ← military `unit_type_id` per-day count, by type
 *   population   ← `population` resource (capacity, NOT per-day)
 *   happiness    ← `happiness` resource (capacity)
 *
 * Out of v1 scope (documented in the phase doc): random-production expected
 * value, set-bonus / motivation / polish multipliers, GB FP-to-next-level.
 */
import type { Plan } from '$lib/types'
import type { CatalogueLookup } from '$lib/editor'
import {
  resolveCycleProduction,
  resolveGBLevelBonus,
  resolveStaticContribution,
} from './catalogue-fields'

export interface ProductionTotals {
  /** Per-day output. */
  coins: number
  supplies: number
  forgePoints: number
  /** Per-day output, keyed by goods type (Marble, Iron, Nanowire, …). */
  goods: Map<string, number>
  /** Per-day output, keyed by unit_type_id. */
  units: Map<string, number>
  /** Capacity totals (NOT per-day). */
  population: number
  happiness: number
}

/** Resource keys that should NOT be bucketed as "goods". Coins/supplies/FP
 *  have their own typed totals; medals/premium/diamonds are out-of-v1
 *  currencies; population/happiness are capacities counted separately. */
const NON_GOODS_KEYS = new Set([
  'money',
  'supplies',
  'strategy_points',
  'population',
  'happiness',
  'premium',
  'diamonds',
  'medals',
])

const PRODUCTIVE_TYPES = new Set([
  'residential',
  'production',
  'goods',
  'culture',
  'military',
  'greatbuilding',
  'main_building',
  'random_production',
  'cultural_goods_production',
  'static_provider',
  'tower',
  'clan_power_production',
])

function emptyTotals(): ProductionTotals {
  return {
    coins: 0,
    supplies: 0,
    forgePoints: 0,
    goods: new Map(),
    units: new Map(),
    population: 0,
    happiness: 0,
  }
}

function addToGoods(map: Map<string, number>, key: string, value: number): void {
  if (!Number.isFinite(value) || value === 0) return
  map.set(key, (map.get(key) ?? 0) + value)
}

/**
 * Apply a ResourceMap (per-day output) into the typed buckets. Unknown
 * resource keys flow into `goods` so a future export shape that introduces
 * a new currency surfaces in the goods total instead of getting silently
 * dropped — matches the conservative `unknown` handling pattern from
 * `lib/catalogue/normalize.ts`.
 */
function applyResources(
  resources: Map<string, number>,
  totals: ProductionTotals,
  bucket: 'flow' | 'capacity',
): void {
  for (const [key, raw] of resources) {
    if (!Number.isFinite(raw) || raw === 0) continue
    if (key === 'population') {
      totals.population += raw
    } else if (key === 'happiness') {
      totals.happiness += raw
    } else if (bucket === 'capacity') {
      // Static capacity-style contributions only contribute to population /
      // happiness in v1; flat per-day flows from staticResources (rare in
      // practice) flow through the cycle path instead.
      continue
    } else if (key === 'money') {
      totals.coins += raw
    } else if (key === 'supplies') {
      totals.supplies += raw
    } else if (key === 'strategy_points') {
      totals.forgePoints += raw
    } else if (NON_GOODS_KEYS.has(key)) {
      // medals / premium / diamonds — not surfaced in v1 totals.
      continue
    } else {
      addToGoods(totals.goods, key, raw)
    }
  }
}

export function computeProduction(
  plan: Plan,
  lookup: CatalogueLookup,
  invalidBuildingIds: ReadonlySet<number>,
  playerEra: string | undefined,
): ProductionTotals {
  const totals = emptyTotals()

  for (const building of plan.buildings) {
    if (invalidBuildingIds.has(building.id)) continue
    const resolved = lookup(building.cityentityId)
    if (!resolved) continue
    if (resolved.isOffGrid || resolved.isRoad) continue
    if (!PRODUCTIVE_TYPES.has(resolved.type)) continue

    // 1. Cycle production (available_products + components production).
    const cycle = resolveCycleProduction(resolved.raw, playerEra)
    applyResources(cycle.resources, totals, 'flow')
    for (const [unitType, perDay] of cycle.units) {
      if (Number.isFinite(perDay) && perDay > 0) {
        totals.units.set(unitType, (totals.units.get(unitType) ?? 0) + perDay)
      }
    }

    // 2. Static contribution (staticResources, provided_happiness, etc).
    const staticContrib = resolveStaticContribution(resolved.raw, playerEra)
    applyResources(staticContrib.resources, totals, 'capacity')

    // 3. GB level bonus, if the placement carries a level. The level field
    //    on PlanBuilding is the placed GB level (and also the active level
    //    for some level-able events; v1 only consumes it for GBs since
    //    event entity_levels reads are deferred). Reading non-GB level
    //    here would be a no-op anyway (resolveGBLevelBonus returns empty
    //    for non-greatbuilding type via production_bonus presence check).
    if (resolved.type === 'greatbuilding' && typeof building.level === 'number') {
      const gbBonus = resolveGBLevelBonus(resolved.raw, building.level)
      applyResources(gbBonus.resources, totals, 'flow')
    }
  }

  return totals
}

/**
 * Total goods across all goods types — convenience for the toolbar tile.
 * Per-type breakdown lives in the SelectionPanel for individual buildings.
 */
export function totalGoods(totals: ProductionTotals): number {
  let sum = 0
  for (const v of totals.goods.values()) sum += v
  return sum
}

/** Total units across all unit types — convenience for the toolbar tile. */
export function totalUnits(totals: ProductionTotals): number {
  let sum = 0
  for (const v of totals.units.values()) sum += v
  return sum
}

/**
 * Module-local memo keyed by `(planId, version)` — same composite-key pattern
 * as `lib/editor/connectivity.ts`. Production compute is heavier than
 * connectivity (per-building catalogue read + multiple resolver passes); the
 * cache shaves it to a Map lookup on every render that doesn't follow a
 * command. The memo invalidates automatically when a command bumps
 * `plan.version`; cross-plan collisions on freshly-loaded plans both at
 * version 0 are avoided by the composite key.
 *
 * `invalidBuildingIds` is NOT part of the cache key — validation runs before
 * stats and is itself version-keyed, so the invalid set for a given version
 * is deterministic. Including it would just bloat the comparison without
 * adding correctness.
 *
 * `playerEra` is part of the key because two consecutive imports could share
 * a planId / version pair through test fixtures and differ only by the era
 * passed in. In practice the editor pins one era per plan, but the cost of
 * including it is negligible.
 */
let memo: {
  planId: number
  version: number
  era: string | undefined
  totals: ProductionTotals
} | null = null

/** Test-only: drop the memo. Production code never needs to call this. */
export function clearProductionMemo(): void {
  memo = null
}

/**
 * Memoised wrapper. Returns the cached `ProductionTotals` (same object
 * reference) when the cache key matches. Use this from the editor / view;
 * use `computeProduction` directly when you need to bypass the memo (rare).
 */
export function getProduction(
  plan: Plan,
  lookup: CatalogueLookup,
  invalidBuildingIds: ReadonlySet<number>,
  playerEra: string | undefined,
): ProductionTotals {
  if (
    memo &&
    memo.planId === plan.id &&
    memo.version === plan.version &&
    memo.era === playerEra
  ) {
    return memo.totals
  }
  const totals = computeProduction(plan, lookup, invalidBuildingIds, playerEra)
  memo = { planId: plan.id, version: plan.version, era: playerEra, totals }
  return totals
}
